import { isBefore } from "date-fns";
import { z } from "zod";

import { formatNumber } from "../../utils";
import { deliveryCostIssue, requiresDeliveryCost } from "../../utils/delivery-cost";

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export const createOfferSchema = (maxQuantity: number, t: TranslateFn) => {
  const required = t("traderDetails.form.mandatoryFieldBlankError");

  const toNumber = (value: unknown) =>
    value === "" || value === undefined || value === null ? undefined : Number(value);

  return z
    .object({
      minimum_quantity: z.preprocess(
        toNumber,
        z
          .number({ error: required })
          .positive(t("directOfferDetails.form.depositNonZero"))
          .max(
            maxQuantity,
            `${t("directOfferDetails.form.depositQuantityExceeded")} ${formatNumber(maxQuantity)}`
          )
      ),
      price: z.preprocess(
        toNumber,
        z.number({ error: required }).positive(t("directOfferDetails.form.priceNonZero"))
      ),
      negotiable: z.custom<{ value: boolean; label: string }>(
        value => !!value && typeof value === "object" && "value" in value,
        { message: required }
      ),
      exchange_date_direct: z
        .date({ error: required })
        .refine(date => !isBefore(date, new Date()), {
          message: t("directOfferDetails.form.invalidExchangeDate"),
        }),
      delivery_location: z.custom<DeliveryLocationOption>(
        value => !!value && typeof value === "object" && "value" in value,
        { message: required }
      ),
      delivery_cost: z.string().optional(),
      comment: z.string().optional(),
      contract_number: z.string().optional(),
    })
    .superRefine(
      (values, ctx) => {
        if (!requiresDeliveryCost(values?.delivery_location?.value)) {
          return;
        }
        const issue = deliveryCostIssue(values.delivery_cost, t);
        if (issue) {
          ctx.addIssue({ code: "custom", path: ["delivery_cost"], message: issue });
        }
      },
      { when: () => true }
    );
};

export type OfferFormValues = {
  minimum_quantity: string;
  price: string;
  negotiable: { value: boolean; label: string } | null;
  exchange_date_direct: Date | null;
  delivery_location: DeliveryLocationOption | null;
  delivery_cost: string;
  comment?: string;
  contract_number?: string;
};

export type DeliveryLocationValue =
  | string
  | {
      deliveryLocationType: string;
      custom_delivery_location: number;
      custom_delivery_location_type: number;
    };

export interface DeliveryLocationOption {
  value: DeliveryLocationValue;
  label: string;
  location?: string;
}
